import { useQuery } from "@tanstack/react-query";
import React from "react";
import { API } from "../API";
import useAgencies from "../hooks/useAgencies";
import useMixin from "../hooks/useMixin";
import { isLoading, Loading } from "../Loading";
import {
  AgencyWithIsChatOnly,
  PatientDocumentTypeId,
  PlanOfCare,
  PlanOfCareWithAnswers,
  POCItem,
  POCItemAnswer,
} from "../messages";
import { CaregiverCertification, VisitInstanceId } from "../messages/ObjectIds";
import { PatientDocumentContentItem } from "../messages/PatientDocuments";
import { AppState } from "../system/AppState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { fmap } from "../utils/fmap";
import { withDefault } from "../utils/withDefault";
import { isDefined } from "./Generic/IsDefined";

export interface PlanOfCareItemWithAnswerAndIsOpen {
  item: POCItem;
  answer: POCItemAnswer | null;
  isOpen: boolean;
}

interface PlanOfCareWithAnswerAndIsOpen {
  poc: PlanOfCare;
  planOfCareType: {
    showPatientSignature: boolean;
  };
  list: PlanOfCareItemWithAnswerAndIsOpen[];
}

export interface InitialPlanOfCare {
  initialPlanOfCare: PlanOfCareWithAnswerAndIsOpen;
  initialDocument: PatientDocumentContentItem[];
  patientDocId: PatientDocumentTypeId;
  initialCaregiverSignature: string | null;
  initialPatientSignature: string | null;
  patientUnsignedReason: string | null;
}

interface Props {
  visitInstanceId: VisitInstanceId;
  caregiver: AppCaregiver | Loading;
  api: API;
  appState: AppState;
}

type State =
  | { type: "Loading" }
  | { type: "Error" }
  | { type: "Ready"; planOfCare: InitialPlanOfCare | null };

const useVisitInstancePlanOfCare = (props: Props) => {
  const agencies = useAgencies(props.appState);
  const mixin = useMixin(props.appState);

  const plansOfCare = useQuery({
    queryKey: ["visit_instance", props.visitInstanceId, "plan_of_care"],
    queryFn: () => props.api.getPlanOfCare(props.visitInstanceId),
  });

  const state = React.useMemo((): State => {
    if (
      isLoading(agencies) ||
      isLoading(mixin.scheduledVisits) ||
      isLoading(props.caregiver) ||
      plansOfCare.isLoading
    ) {
      return { type: "Loading" };
    }

    const visit = mixin.scheduledVisits.scheduledVisitDetails.find(
      (x) => x.id === props.visitInstanceId
    );

    if (visit === undefined) {
      return { type: "Ready", planOfCare: null };
    }

    const agency = agencies.get(visit.publicVisitDetails.agency.id);

    if (agency === undefined || !isCaregiverCertifiedInAgency(props.caregiver, agency.agency)) {
      return { type: "Ready", planOfCare: null };
    }

    if (plansOfCare.data === undefined) {
      return { type: "Ready", planOfCare: null };
    }

    return {
      type: "Ready",
      planOfCare: fmap(plansOfCare.data.planOfCareWithAnswers, parsePlanOfCare),
    };
  }, [
    agencies,
    mixin.scheduledVisits,
    plansOfCare.data,
    plansOfCare.isLoading,
    props.caregiver,
    props.visitInstanceId,
  ]);

  return {
    planOfCareState: state,
    _internals: {
      initPlanOfCareState: plansOfCare.refetch,
    },
  };
};

function isCaregiverCertifiedInAgency(caregiver: AppCaregiver, agency: AgencyWithIsChatOnly) {
  const certifiedCertifications = [
    CaregiverCertification.wrap("RN"),
    CaregiverCertification.wrap("LPN"),
  ];

  const assoc = caregiver.agenciesCaregiverCertifications.find((x) => x.agencyName === agency.name);

  if (!isDefined(assoc)) {
    throw new Error(
      `Could not find association between caregiver ${caregiver.id} and agency ${agency.name}`
    );
  }

  return assoc.certifications.some((x) => certifiedCertifications.indexOf(x) > -1);
}

function parsePlanOfCare(planOfCare: PlanOfCareWithAnswers): InitialPlanOfCare {
  return {
    initialPlanOfCare: {
      list: planOfCare.planOfCareItems.map((value) => ({
        item: value,
        answer: null,
        isOpen: false,
      })),
      planOfCareType: planOfCare.planOfCareType,
      poc: planOfCare.poc,
    },
    patientDocId: PatientDocumentTypeId.wrap(-12345),
    initialDocument: planOfCare.docItems,
    initialCaregiverSignature: fmap(planOfCare.poc.caregiverSignature, (x) => x.data),
    initialPatientSignature: fmap(planOfCare.poc.patientSignature, (x) => x.data),
    patientUnsignedReason: withDefault(planOfCare.poc.patientUnsignedReason, null),
  };
}

export default useVisitInstancePlanOfCare;
